"use client";

import { getAnalyticsInstance } from "@/lib/firebase-client";
import type { AccountTier } from "./types";

/**
 * Analytics event names
 */
export const AnalyticsEvents = {
  // User Actions
  USER_SIGNED_UP: "user_signed_up",
  USER_LOGGED_IN: "user_logged_in",
  USER_LOGGED_OUT: "user_logged_out",
  
  // Tier Conversions
  TIER_UPGRADED: "tier_upgraded",
  TIER_DOWNGRADED: "tier_downgraded",
  CHECKOUT_STARTED: "checkout_started",
  PAYMENT_COMPLETED: "payment_completed",
  PAYMENT_FAILED: "payment_failed",
  SUBSCRIPTION_CANCELLED: "subscription_cancelled",
  
  // Recipe Box Actions
  RECIPE_BOX_CREATED: "recipe_box_created",
  RECIPE_BOX_UPDATED: "recipe_box_updated",
  RECIPE_BOX_DELETED: "recipe_box_deleted",
  
  // Recipe Actions
  RECIPE_CREATED: "recipe_created",
  RECIPE_UPDATED: "recipe_updated",
  RECIPE_DELETED: "recipe_deleted",
  RECIPE_CLONED: "recipe_cloned",
  RECIPE_EXPORTED: "recipe_exported",
  RECIPE_SHARED: "recipe_shared",
  RECIPE_IMPORTED: "recipe_imported",
  RECIPE_FAVORITED: "recipe_favorited",
  RECIPE_UNFAVORITED: "recipe_unfavorited",
  
  // Member Management
  MEMBER_INVITED: "member_invited",
  MEMBER_ADDED: "member_added",
  MEMBER_REMOVED: "member_removed",
  MEMBER_ROLE_CHANGED: "member_role_changed",
  
  // Search & Filtering
  SEARCH_PERFORMED: "search_performed",
  FILTER_APPLIED: "filter_applied",
  
  // Collections & Categories
  COLLECTION_CREATED: "collection_created",
  COLLECTION_DELETED: "recipe_added_to_collection",
  RECIPE_ADDED_TO_COLLECTION: "recipe_added_to_collection",
  RECIPE_REMOVED_FROM_COLLECTION: "recipe_removed_from_collection",
  CATEGORY_ADDED: "category_added",
  
  // Support
  SUPPORT_REQUEST_SENT: "support_request_sent",
  
  // Admin Actions
  ADMIN_SETTINGS_UPDATED: "admin_settings_updated",
} as const;

/**
 * Track a custom analytics event
 */
export async function trackEvent(
  eventName: string,
  eventParams?: Record<string, string | number | boolean | null>,
): Promise<void> {
  try {
    const analytics = await getAnalyticsInstance();
    if (!analytics) {
      // Analytics not available (e.g., in development, not supported)
      return;
    }

    const { logEvent } = await import("firebase/analytics");
    logEvent(analytics, eventName, eventParams);
  } catch (error) {
    // Silently fail in development or if analytics is not available
    if (import.meta.env.DEV) {
      console.debug("[Analytics] Event tracking skipped:", eventName, error);
    }
  }
}

/**
 * Track user sign up
 */
export function trackUserSignUp(method: "email" | "google" | "apple"): void {
  trackEvent(AnalyticsEvents.USER_SIGNED_UP, { method });
}

/**
 * Track user login
 */
export function trackUserLogin(method: "email" | "google" | "apple"): void {
  trackEvent(AnalyticsEvents.USER_LOGGED_IN, { method });
}

/**
 * Track user logout
 */
export function trackUserLogout(): void {
  trackEvent(AnalyticsEvents.USER_LOGGED_OUT);
}

/**
 * Track tier upgrade
 */
export function trackTierUpgrade(
  fromTier: AccountTier | undefined,
  toTier: AccountTier,
): void {
  trackEvent(AnalyticsEvents.TIER_UPGRADED, {
    from_tier: fromTier || "free",
    to_tier: toTier,
  });
}

/**
 * Track tier downgrade
 */
export function trackTierDowngrade(
  fromTier: AccountTier,
  toTier: AccountTier,
): void {
  trackEvent(AnalyticsEvents.TIER_DOWNGRADED, {
    from_tier: fromTier,
    to_tier: toTier,
  });
}

/**
 * Track checkout started
 */
export function trackCheckoutStarted(tier: AccountTier): void {
  trackEvent(AnalyticsEvents.CHECKOUT_STARTED, { tier });
}

/**
 * Track payment completed
 */
export function trackPaymentCompleted(tier: AccountTier, amount?: number): void {
  trackEvent(AnalyticsEvents.PAYMENT_COMPLETED, {
    tier,
    ...(amount !== undefined && { amount }),
  });
}

/**
 * Track payment failed
 */
export function trackPaymentFailed(tier: AccountTier, reason?: string): void {
  trackEvent(AnalyticsEvents.PAYMENT_FAILED, {
    tier,
    ...(reason && { reason }),
  });
}

/**
 * Track subscription cancellation
 */
export function trackSubscriptionCancelled(tier: AccountTier): void {
  trackEvent(AnalyticsEvents.SUBSCRIPTION_CANCELLED, { tier });
}

/**
 * Track recipe box creation
 */
export function trackRecipeBoxCreated(): void {
  trackEvent(AnalyticsEvents.RECIPE_BOX_CREATED);
}

/**
 * Track recipe box update
 */
export function trackRecipeBoxUpdated(): void {
  trackEvent(AnalyticsEvents.RECIPE_BOX_UPDATED);
}

/**
 * Track recipe creation
 */
export function trackRecipeCreated(hasImage: boolean, category?: string): void {
  trackEvent(AnalyticsEvents.RECIPE_CREATED, {
    has_image: hasImage,
    ...(category && { category }),
  });
}

/**
 * Track recipe update
 */
export function trackRecipeUpdated(): void {
  trackEvent(AnalyticsEvents.RECIPE_UPDATED);
}

/**
 * Track recipe deletion
 */
export function trackRecipeDeleted(): void {
  trackEvent(AnalyticsEvents.RECIPE_DELETED);
}

/**
 * Track recipe cloning
 */
export function trackRecipeCloned(
  sameBox: boolean,
  recipeCount?: number,
): void {
  trackEvent(AnalyticsEvents.RECIPE_CLONED, {
    same_box: sameBox,
    ...(recipeCount !== undefined && { recipe_count: recipeCount }),
  });
}

/**
 * Track recipe export
 */
export function trackRecipeExported(format: string): void {
  trackEvent(AnalyticsEvents.RECIPE_EXPORTED, { format });
}

/**
 * Track recipe sharing
 */
export function trackRecipeShared(isPublic: boolean, hasPassword: boolean): void {
  trackEvent(AnalyticsEvents.RECIPE_SHARED, {
    is_public: isPublic,
    has_password: hasPassword,
  });
}

/**
 * Track recipe import
 */
export function trackRecipeImported(source: "url" | "file"): void {
  trackEvent(AnalyticsEvents.RECIPE_IMPORTED, { source });
}

/**
 * Track recipe favorite toggle
 */
export function trackRecipeFavorite(isFavorite: boolean): void {
  trackEvent(
    isFavorite ? AnalyticsEvents.RECIPE_FAVORITED : AnalyticsEvents.RECIPE_UNFAVORITED,
  );
}

/**
 * Track member invitation
 */
export function trackMemberInvited(role: string): void {
  trackEvent(AnalyticsEvents.MEMBER_INVITED, { role });
}

/**
 * Track member added
 */
export function trackMemberAdded(role: string): void {
  trackEvent(AnalyticsEvents.MEMBER_ADDED, { role });
}

/**
 * Track member removed
 */
export function trackMemberRemoved(): void {
  trackEvent(AnalyticsEvents.MEMBER_REMOVED);
}

/**
 * Track member role change
 */
export function trackMemberRoleChanged(
  fromRole: string,
  toRole: string,
): void {
  trackEvent(AnalyticsEvents.MEMBER_ROLE_CHANGED, {
    from_role: fromRole,
    to_role: toRole,
  });
}

/**
 * Track search performed
 */
export function trackSearchPerformed(
  query: string,
  resultCount: number,
  scope: "box" | "all",
): void {
  trackEvent(AnalyticsEvents.SEARCH_PERFORMED, {
    query_length: query.length,
    result_count: resultCount,
    scope,
  });
}

/**
 * Track filter applied
 */
export function trackFilterApplied(filterType: string, filterCount: number): void {
  trackEvent(AnalyticsEvents.FILTER_APPLIED, {
    filter_type: filterType,
    filter_count: filterCount,
  });
}

/**
 * Track collection created
 */
export function trackCollectionCreated(isCrossBox: boolean): void {
  trackEvent(AnalyticsEvents.COLLECTION_CREATED, { is_cross_box: isCrossBox });
}

/**
 * Track recipe added to collection
 */
export function trackRecipeAddedToCollection(): void {
  trackEvent(AnalyticsEvents.RECIPE_ADDED_TO_COLLECTION);
}

/**
 * Track support request sent
 */
export function trackSupportRequestSent(): void {
  trackEvent(AnalyticsEvents.SUPPORT_REQUEST_SENT);
}

/**
 * Track admin settings update
 */
export function trackAdminSettingsUpdated(settingType: string): void {
  trackEvent(AnalyticsEvents.ADMIN_SETTINGS_UPDATED, { setting_type: settingType });
}
